/**
 * Undo/redo for the drawing surface.
 *
 * Geoman has none of its own — the main product builds it by hand in `useUndoRedo`
 * (`frontend/src/hooks/useUndoRedo.ts`), and this is that algorithm, past/present/future,
 * unchanged. What changed is the shape: there it is a React hook because a React component
 * owns the map; here the drawing surface is driven imperatively from a `DrawController`, so
 * a hook would have nothing to render and no component to live in.
 *
 * The one behaviour NOT taken from the source: it restores snapshots by wiping every polygon
 * and re-adding them from GeoJSON, guarded by an `isRestoring` flag cleared one animation
 * frame later. That is a bet on Leaflet dispatching all of its `layeradd` events inside a
 * single frame. Here restoration is synchronous and the guard is a counter, so there is no
 * frame to lose the race in (map/geoman.ts).
 */

export interface HistoryStore<T> {
  present: () => T;
  push: (value: T) => void;
  undo: () => T | null;
  redo: () => T | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  reset: (value: T) => void;
}

export const createHistory = <T>(initial: T): HistoryStore<T> => {
  let past: T[] = [];
  let present: T = initial;
  let future: T[] = [];

  return {
    present: () => present,
    push: (value) => {
      past = [...past, present];
      present = value;
      future = [];
    },
    undo: () => {
      if (past.length === 0) return null;
      const previous = past[past.length - 1] as T;
      past = past.slice(0, -1);
      future = [present, ...future];
      present = previous;
      return present;
    },
    redo: () => {
      if (future.length === 0) return null;
      const next = future[0] as T;
      future = future.slice(1);
      past = [...past, present];
      present = next;
      return present;
    },
    canUndo: () => past.length > 0,
    canRedo: () => future.length > 0,
    reset: (value) => {
      past = [];
      present = value;
      future = [];
    },
  };
};
